import { apiJson } from './apiFetch';
import type {
  CreateShiftFillRequestPayload,
  ShiftFillRequestStatus,
  ShiftFillRequestDetail,
  ShiftFillRequestSummary,
  ShiftOfferPreview,
  ShiftOfferPreviewPayload,
  StoreEmployee,
  UserPhoneNumber,
} from './shiftFill.types';

const OPEN_STATUSES: readonly ShiftFillRequestStatus[] = ['draft', 'live'];

/** A request still waiting for answers; the API refuses to cancel any other. */
export const isOpenRequest = (status: ShiftFillRequestStatus): boolean => OPEN_STATUSES.includes(status);

/** React Query key root of the shift fill requests; invalidate it after a create or a cancel. */
export const SHIFT_FILL_REQUESTS_QUERY_KEY = ['shift-fill-requests'] as const;

export const getShiftFillRequestsQueryKey = (storeId: string) => [...SHIFT_FILL_REQUESTS_QUERY_KEY, storeId] as const;

export const getShiftFillRequestQueryKey = (id: string) => [...SHIFT_FILL_REQUESTS_QUERY_KEY, 'detail', id] as const;

export const getStoreEmployeesQueryKey = (storeId: string) => ['store-employees', storeId] as const;

export const getShiftOfferPreviewQueryKey = ({ storeId, startsAt, endsAt, userId }: ShiftOfferPreviewPayload) =>
  ['shift-offer-preview', storeId, startsAt, endsAt, userId] as const;

export const getUserPhoneNumberQueryKey = (userId: string) => ['user-phone-number', userId] as const;

const requestPath = (id: string) => `/shift-fill-requests/${encodeURIComponent(id)}`;

const userPhonePath = (userId: string) => `/users/${encodeURIComponent(userId)}/phone`;

export const getStoreEmployees = (storeId: string): Promise<StoreEmployee[]> =>
  apiJson<StoreEmployee[]>(`/stores/${encodeURIComponent(storeId)}/employees`);

export const getShiftFillRequests = (storeId: string): Promise<ShiftFillRequestSummary[]> =>
  apiJson<ShiftFillRequestSummary[]>(`/shift-fill-requests?storeId=${encodeURIComponent(storeId)}`);

export const getShiftFillRequest = (id: string): Promise<ShiftFillRequestDetail> =>
  apiJson<ShiftFillRequestDetail>(requestPath(id));

/** 422 `unknown-employees` when someone left the store meanwhile. */
export const createShiftFillRequest = (payload: CreateShiftFillRequestPayload): Promise<ShiftFillRequestDetail> =>
  apiJson<ShiftFillRequestDetail>('/shift-fill-requests', { method: 'POST', body: JSON.stringify(payload) });

/** 409 `shift-fill-request-closed` once the request is filled, cancelled or expired. */
export const cancelShiftFillRequest = (id: string): Promise<ShiftFillRequestDetail> =>
  apiJson<ShiftFillRequestDetail>(`${requestPath(id)}/cancel`, { method: 'POST' });

/** The WhatsApp offer as it would be sent; 503 `offer-preview-unavailable` while the template cannot be read. */
export const previewShiftOffer = (payload: ShiftOfferPreviewPayload): Promise<ShiftOfferPreview> =>
  apiJson<ShiftOfferPreview>('/shift-fill-requests/preview', { method: 'POST', body: JSON.stringify(payload) });

export const getUserPhoneNumber = (userId: string): Promise<UserPhoneNumber> =>
  apiJson<UserPhoneNumber>(userPhonePath(userId));

/** An empty string clears the number; the API accepts international (E.164) numbers only. */
export const updateUserPhoneNumber = (userId: string, phoneNumber: string): Promise<UserPhoneNumber> =>
  apiJson<UserPhoneNumber>(userPhonePath(userId), { method: 'PUT', body: JSON.stringify({ phoneNumber }) });
